/**
 * Exit Popup.
 *
 * Standalone exit-intent newsletter popup. Separate from PopupManager
 * (popup.js): different triggers and frequency logic, so the small cookie
 * helpers are duplicated here on purpose rather than refactoring shared code.
 *
 * Frequency is tracked per page: the "shown" cookie is scoped by the current
 * page ID (data-exit-popup-scope), so dismissing the popup on one page leaves
 * it free to appear on another the visitor has not seen yet.
 *
 * Collision rule: if the smoobu_popup CPT is present on the page (`.popup`),
 * the Exit Popup yields and never arms — unless the "Show even if other popups
 * are present" option is on (data-exit-popup-ignore-popups="1"), which forces
 * it to run regardless.
 */

const COOKIE_BASE = 'exitPopupShown';
const MOBILE_QUERY = '( max-width: 767px )';
const SCROLL_UP_DELTA = 10;
const SCROLL_NEAR_TOP = 120;

/**
 * Read a cookie value by name.
 *
 * @param {string} name Cookie name.
 * @return {string} Decoded value or empty string.
 */
const getCookie = ( name ) => {

	const nameEq = `${ name }=`;
	const cookies = document.cookie ? document.cookie.split( ';' ) : [];
	for ( const cookie of cookies ) {

		const trimmed = cookie.trim();
		if ( trimmed.startsWith( nameEq ) ) {

			return decodeURIComponent( trimmed.slice( nameEq.length ) );

		}

	}
	return '';

};

/**
 * Set a cookie with an expiration in days.
 *
 * @param {string} name Cookie name.
 * @param {string} value Cookie value.
 * @param {number} days Expiration in days.
 * @return {void}
 */
const setCookie = ( name, value, days ) => {

	const safeDays = Math.min( Math.max( 0, days ), 365 * 10 );
	const expires = new Date();
	expires.setTime( expires.getTime() + safeDays * 24 * 60 * 60 * 1000 );
	document.cookie = `${ name }=${ encodeURIComponent( value ) }; expires=${ expires.toUTCString() }; path=/; SameSite=Lax`;

};

/**
 * Check whether the current viewport is mobile-sized.
 *
 * @return {boolean}
 */
const isMobileViewport = () => {

	return window.matchMedia( MOBILE_QUERY ).matches;

};

/**
 * Initialize the Exit Popup on the current page.
 *
 * @return {void}
 */
export const initExitPopup = () => {

	const popup = document.querySelector( '[data-exit-popup]' );
	if ( ! popup ) {

		return;

	}

	// Never two popups at once: the CPT popup takes precedence — unless the
	// "Show even if other popups are present" option forces the Exit Popup on.
	const ignoreOtherPopups = popup.dataset.exitPopupIgnorePopups === '1';
	if ( ! ignoreOtherPopups && document.querySelector( '.popup' ) ) {

		return;

	}

	// Scope the cookie per page so a dismissal on one page does not suppress
	// the popup on another. The scope is the current page ID (normalized to
	// the default language server-side); fall back to a shared cookie if it
	// is missing for any reason.
	const scope = ( popup.dataset.exitPopupScope || '' ).replace( /[^\w-]/g, '' );
	const cookieName = scope ? `${ COOKIE_BASE }_${ scope }` : COOKIE_BASE;

	// Already shown within the configured window for this page.
	if ( getCookie( cookieName ) === '1' ) {

		return;

	}

	const overlay = popup.querySelector( '[data-exit-popup-overlay]' );
	const closeButton = popup.querySelector( '[data-exit-popup-close]' );

	const parsedFrequency = Number.parseInt( popup.dataset.exitPopupFrequency || '7', 10 );
	const frequencyDays = Number.isFinite( parsedFrequency ) && parsedFrequency > 0 ? parsedFrequency : 7;

	const triggerListeners = [];
	let shown = false;
	let armed = false;
	let lastScrollY = window.scrollY || window.pageYOffset || 0;

	/**
	 * Register an event listener and remember it for later removal.
	 *
	 * @param {EventTarget} target Event target.
	 * @param {string} type Event type.
	 * @param {Function} handler Handler.
	 * @param {boolean|Object} options Listener options.
	 * @return {void}
	 */
	const addTrigger = ( target, type, handler, options ) => {

		target.addEventListener( type, handler, options );
		triggerListeners.push( { target, type, handler, options } );

	};

	/**
	 * Remove every trigger / first-interaction listener.
	 *
	 * @return {void}
	 */
	const removeTriggers = () => {

		while ( triggerListeners.length ) {

			const { target, type, handler, options } = triggerListeners.pop();
			target.removeEventListener( type, handler, options );

		}

	};

	/**
	 * Show the popup, persist the cookie and detach all triggers.
	 *
	 * @return {void}
	 */
	const show = () => {

		if ( shown ) {

			return;

		}
		shown = true;
		popup.classList.add( 'is-active' );
		setCookie( cookieName, '1', frequencyDays );
		removeTriggers();

	};

	/**
	 * Hide the popup (the cookie was already set on show).
	 *
	 * @return {void}
	 */
	const hide = () => {

		popup.classList.remove( 'is-active' );

	};

	/**
	 * Desktop trigger: cursor leaving the viewport through any edge.
	 *
	 * @param {MouseEvent} event Mouse event.
	 * @return {void}
	 */
	const onMouseOut = ( event ) => {

		if ( event.relatedTarget || event.toElement ) {

			return;

		}

		const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
		const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

		if (
			event.clientY <= 0 ||
			event.clientX <= 0 ||
			event.clientX >= viewportWidth ||
			event.clientY >= viewportHeight
		) {

			show();

		}

	};

	/**
	 * Trigger when the tab/window becomes hidden.
	 *
	 * @return {void}
	 */
	const onVisibilityChange = () => {

		if ( document.hidden ) {

			show();

		}

	};

	/**
	 * Trigger when the window loses focus (switching app/window).
	 *
	 * @return {void}
	 */
	const onBlur = () => {

		show();

	};

	/**
	 * Mobile trigger: a quick upward scroll near the top of the page.
	 *
	 * @return {void}
	 */
	const onScroll = () => {

		const y = window.scrollY || window.pageYOffset || 0;
		if ( isMobileViewport() && y < lastScrollY - SCROLL_UP_DELTA && y < SCROLL_NEAR_TOP ) {

			show();

		}
		lastScrollY = y;

	};

	/**
	 * Mobile trigger: the page is being unloaded / backgrounded.
	 *
	 * @return {void}
	 */
	const onPageHide = () => {

		show();

	};

	/**
	 * Arm all exit-intent triggers (after the first user interaction).
	 *
	 * @return {void}
	 */
	const arm = () => {

		if ( armed ) {

			return;

		}
		armed = true;
		addTrigger( document, 'mouseout', onMouseOut );
		addTrigger( document, 'visibilitychange', onVisibilityChange );
		addTrigger( window, 'blur', onBlur );
		addTrigger( window, 'scroll', onScroll, { passive: true } );
		addTrigger( window, 'pagehide', onPageHide );

	};

	/**
	 * First real interaction: arm the triggers, then drop these listeners.
	 *
	 * @return {void}
	 */
	const onFirstInteraction = () => {

		arm();

	};

	/**
	 * Close on the Escape key.
	 *
	 * @param {KeyboardEvent} event Keyboard event.
	 * @return {void}
	 */
	const onKeydown = ( event ) => {

		if ( event.key === 'Escape' || event.keyCode === 27 ) {

			hide();

		}

	};

	/**
	 * Close when the overlay (outside the dialog) is clicked.
	 *
	 * @param {MouseEvent} event Mouse event.
	 * @return {void}
	 */
	const onOverlayClick = ( event ) => {

		if ( event.target === overlay ) {

			hide();

		}

	};

	// Arm only after a genuine interaction to avoid false triggers on load.
	addTrigger( document, 'mousemove', onFirstInteraction, { once: true } );
	addTrigger( document, 'scroll', onFirstInteraction, { once: true } );
	addTrigger( document, 'keydown', onFirstInteraction, { once: true } );
	addTrigger( document, 'touchstart', onFirstInteraction, { once: true, passive: true } );

	// Close handlers: button, overlay, Escape.
	if ( closeButton ) {

		closeButton.addEventListener( 'click', hide );

	}
	if ( overlay ) {

		overlay.addEventListener( 'click', onOverlayClick );

	}
	document.addEventListener( 'keydown', onKeydown );

};
